import { useQuery } from '@apollo/client/react';
import { GetProfileDocument } from '@/graphql';
import { useMemo } from 'react';

/**
 * Hook to fetch the current user's profile information.
 * Uses GetProfileQuery to retrieve complete user profile data including account details.
 *
 * @returns Object with profile data, loading state, error, and refetch function
 *
 * @example
 * ```tsx
 * const { profile, loading, error, refetch } = useProfile();
 *
 * if (loading) return <div>Loading...</div>;
 * if (error) return <div>Error: {error.message}</div>;
 * if (!profile) return <div>Profile not found</div>;
 *
 * return <ProfileDetail profile={profile} />;
 * ```
 */
export function useProfile() {
  const { data, loading, error, refetch } = useQuery(GetProfileDocument, {
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first'
  });

  const profile = useMemo(() => data?.profile ?? null, [data?.profile]);

  return {
    profile,
    loading,
    error,
    refetch
  };
}
